import {
  ElementRef,
  inject,
  Injectable,
  OnDestroy,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';

import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';

import {
  debounceTime,
  distinctUntilChanged,
  Observable,
  startWith,
  Subject,
  Subscription,
} from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { Trip } from './trip';
import { Config } from '../../core/configs/config';
import { ESvgIcons } from '../../core/enums/svg-icons';

import { AuthService } from '../../core/services/auth.service';
import { TripWizardStateService } from '../components/trip-wizard/trip-wizard-state.service';

@Injectable()
export abstract class TripBase implements OnDestroy {
  public startInBoxFocused: WritableSignal<boolean> = signal(false);
  public searchQueryStartInChanged: Subject<string> = new Subject<string>();
  public searchQueryEndInChanged: Subject<string> = new Subject<string>();
  public startInDestinationOptions$: Observable<any[]>;
  public endInDestinationOptions$: Observable<any[]>;
  public isDefaultEndInView: boolean = true;
  public defaultView: boolean = true;
  public isInsideOverlay: boolean = false;
  @ViewChild('endInput') readonly endInputRef: ElementRef;
  protected trip: WritableSignal<Trip>;
  protected readonly eSvgIcons = ESvgIcons;

  protected tripWizardStateService = inject(TripWizardStateService);
  protected authService = inject(AuthService);
  protected httpClient = inject(HttpClient);
  protected router = inject(Router);

  protected subs: Subscription;

  public ngOnDestroy() {
    this.subs?.unsubscribe();
  }

  public handleDefaultViewSwitcher(isDefaultView: boolean): void {
    this.defaultView = isDefaultView;
    this.trip.set(
      this.trip().updateTrip({
        isPlanningTripLocation: isDefaultView,
      }),
    );
    this.updateTripState();
  }

  public inputChanged(
    event: Event,
    stream: Subject<string>,
    endInInput: boolean = false,
  ): void {
    const input = event.target as HTMLInputElement;
    stream.next(input.value);

    if (endInInput) {
      this.isDefaultEndInView = false;
    }
  }

  public optionSelected(
    ev: MatAutocompleteSelectedEvent,
    prefix: 'starting' | 'ending',
  ): void {
    const data = ev.option.value;
    const payload = {
      [prefix + 'Label']: data.label,
      [prefix + 'LocationId']: data.val,
      [prefix + 'Country']: data.country_id,
    };
    this.trip.set(this.trip().updateTrip(payload));
    this.updateTripState();
    this.isInsideOverlay = false;
    if (prefix === 'starting') {
      this.setupEndInOptions();
      setTimeout(() => this.setFocusToEndInField(), 150);
    }
  }

  public exploreCitySelected(ev: MatAutocompleteSelectedEvent): void {
    const data = ev.option.value;
    const payload = {
      id: data.val,
      url: data.url,
      label: data.label,
      countryId: data.country_id,
    };
    this.trip.set(this.trip().updateTrip({ cityInfo: payload }));
    this.updateTripState();
    this.isInsideOverlay = false;
  }

  public setFocusToStartInField(input: HTMLElement): void {
    this.startInBoxFocused.set(true);
    input.focus();
  }

  public setFocusToEndInField(): void {
    this.isDefaultEndInView = true;
    const endInInput: HTMLInputElement =
      document.querySelector('#end-in-input');
    if (endInInput) {
      endInInput.focus();
      this.isInsideOverlay = true;
    }
  }

  public inputFocusOut(auto: MatAutocomplete, prefix: 'starting' | 'ending') {
    this.isInsideOverlay = false;
    if (auto.isOpen || this.trip()[`${prefix}Label`]) return;
    this.trip.update((trip) => trip.restoreLocations());
  }

  public cleanSelection(prefix: string, locationId: string): void {
    if (locationId) {
      const trip = this.trip();
      if (prefix === 'starting') {
        trip.destroyStartInLocation();
        this.setupStartInOptions();
      } else {
        trip.destroyEndInLocation();
      }
      this.trip.set(new Trip(trip));
    }
  }

  public filterByAirport(
    destinations: any[],
    shouldFilterByAirport: boolean,
  ): any[] | null {
    let arr: any[];
    if (shouldFilterByAirport) {
      arr = destinations.length
        ? destinations.filter((i) => i.isMajorAirport)
        : [];
    } else {
      arr = destinations.length
        ? destinations.filter((i) => !i.isMajorAirport)
        : [];
    }
    return arr.length ? arr : null;
  }

  public updateTripState(): void {
    this.tripWizardStateService.currentTripState = new Trip({
      ...this.trip(),
    });
  }

  public displayWith = (value: any) => value?.label ?? value;

  protected setupStartInOptions(): void {
    this.startInDestinationOptions$ = this.searchQueryStartInChanged.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((startInQuery: string) => {
        return this.httpClient.get<any>(
          `${Config.apiUrl}/actions/autocomplete.php?cc=1&term=${startInQuery}`,
        );
      }),
    );
  }

  protected setupEndInOptions(): void {
    this.endInDestinationOptions$ = this.searchQueryEndInChanged.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      startWith(this.trip()?.startingLabel || ''),
      switchMap((endInQuery: string) => {
        return this.httpClient.get<any>(
          `${Config.apiUrl}/actions/autocomplete.php?cc=${this.trip()?.startingCountry || 1}&term=${endInQuery}&airports=1`,
        );
      }),
    );
  }
}
